import React, { useEffect, useRef, useState } from "react";
import { FiEdit2, FiSearch, FiTrash2, FiUser } from "react-icons/fi";
import { IoLocationOutline } from "react-icons/io5";
import { FaCheckCircle, FaRegCircle } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAddresses, updateAddress } from "../../utils/accountApi";
import axiosInstance from "../../utils/axiosInstance";
import "./account.css";
import { useDispatch } from "react-redux";
import { clearWishlist } from "../../redux/wishlistSlice";

const API_KEY = "AIzaSyDD8frd15FoMhemosVqGvVBCHaRjLgNszc";

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const emptyForm = {
  name: "", address_line_1: "", address_line_2: "", flat_no: "",
  landmark: "", zipcode: "", mobileno: "", country: "India",
  state: "", city: "", latitude: "", longitude: "",
};

// steps: "default" → "add" (search + map) → "address" (form)

function AddressesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const addressType = location.state?.addressType;
  const userId = localStorage.getItem("userid");
  const userName = localStorage.getItem("name") || "Customer";

  // ── address list state ──
  const [addresses, setAddresses] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [userProfile, setUserProfile] = useState(null);

  // ── flow step ──
  const [step, setStep] = useState("default");
  const [editId, setEditId] = useState(null);

  // ── zipcode check ──
  const [zipcodeCheck, setZipcodeCheck] = useState(() => localStorage.getItem("savedZipcode") || "");
  const [zipcodeStatus, setZipcodeStatus] = useState(() => localStorage.getItem("savedZipcode") ? { status: "Available" } : null); // null | { status, zipcode }
  const [checkingZip, setCheckingZip] = useState(false);

  // ── map / search ──
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState(null); // { lat, lng, address }
  const [geoLocating, setGeoLocating] = useState(false);
  const [geoError, setGeoError] = useState("");
  const searchRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const infoWindowRef = useRef(null);

  // ── form ──
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) return null;
    const msg = Array.isArray(fieldErrors[field]) ? fieldErrors[field][0] : fieldErrors[field];
    return (
      <span style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px", display: "block", fontWeight: "500", textTransform: "none" }}>
        {msg}
      </span>
    );
  };

  // ═══════════════════════════════════════════════════════
  // Load Google Maps script once
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (window.google) return setScriptLoaded(true);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  const resetMapRefs = () => {
    if (markerRef.current) markerRef.current.setMap(null);
    if (infoWindowRef.current) infoWindowRef.current.close();
    mapRef.current = null;
    markerRef.current = null;
    infoWindowRef.current = null;
  };

  const resetLocationPicker = () => {
    setQuery("");
    setSuggestions([]);
    setSelected(null);
    setGeoError("");
    resetMapRefs();
  };

  const processGeocodeResults = (res, lat, lng, fallbackAddress = "") => {
    if (!res || res.length === 0) {
      if (fallbackAddress) {
        setSelected({ lat: Number(lat), lng: Number(lng), address: fallbackAddress, components: [] });
        setQuery(fallbackAddress);
      }
      return;
    }

    const bestWithZip = res.find(r => r.address_components?.some(c => c.types?.includes("postal_code"))) || res[0];
    const addr = bestWithZip.formatted_address || res[0].formatted_address || fallbackAddress;

    const components = [...(bestWithZip.address_components || res[0].address_components || [])];
    const requiredTypes = ["postal_code", "locality", "administrative_area_level_1", "country"];

    requiredTypes.forEach(reqType => {
      const hasType = components.some(c => c.types?.includes(reqType));
      if (!hasType) {
        for (const r of res) {
          const found = r.address_components?.find(c => c.types?.includes(reqType));
          if (found) {
            components.push(found);
            break;
          }
        }
      }
    });

    setSelected({ lat: Number(lat), lng: Number(lng), address: addr, components });
    setQuery(addr);
  };

  const selectAustinFallback = (msg = "") => {
    const lat = 30.2672;
    const lng = -97.7431;
    if (msg) setGeoError(msg);
    const waitForGoogle = (attempts = 0) => {
      if (window.google?.maps) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (res, status) => {
          setGeoLocating(false);
          if (status === "OK" && res && res.length > 0) {
            processGeocodeResults(res, lat, lng, "Austin, TX, USA");
          } else {
            setSelected({ lat, lng, address: "Austin, TX, USA", components: [] });
            setQuery("Austin, TX, USA");
          }
        });
      } else if (attempts < 20) {
        setTimeout(() => waitForGoogle(attempts + 1), 300);
      } else {
        setGeoLocating(false);
        setSelected({ lat, lng, address: "Austin, TX, USA", components: [] });
        setQuery("Austin, TX, USA");
      }
    };
    waitForGoogle();
  };

  // ═══════════════════════════════════════════════════════
  // Geolocation — auto-detect on add step
  // ═══════════════════════════════════════════════════════
  const detectCurrentLocation = () => {
    if (!navigator.geolocation) {
      selectAustinFallback("Geolocation is not supported by your browser. Showing Austin, USA location.");
      return;
    }
    setGeoLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const waitForGoogle = (attempts = 0) => {
          if (window.google?.maps) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (res, status) => {
              setGeoLocating(false);
              if (status === "OK" && res && res.length > 0) {
                processGeocodeResults(res, latitude, longitude);
              } else {
                selectAustinFallback("Could not determine current address. Showing Austin, USA location.");
              }
            });
          } else if (attempts < 20) {
            setTimeout(() => waitForGoogle(attempts + 1), 300);
          } else {
            selectAustinFallback("Maps not ready. Showing Austin, USA location.");
          }
        };
        waitForGoogle();
      },
      (err) => {
        if (err.code === 1) {
          selectAustinFallback("Location permission denied. Showing Austin, USA location.");
        } else {
          selectAustinFallback("Unable to fetch location. Showing Austin, USA location.");
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    if (step === "add") {
      detectCurrentLocation();
    }
  }, [step]);

  const placeMarker = (position) => {
    if (!mapRef.current || !window.google) return;

    if (markerRef.current) {
      markerRef.current.setPosition(position);
      mapRef.current.panTo(position);
      mapRef.current.setZoom(16);
    } else {
      markerRef.current = new window.google.maps.Marker({
        position,
        map: mapRef.current,
        animation: window.google.maps.Animation.DROP,
      });
      mapRef.current.panTo(position);
      mapRef.current.setZoom(16);
    }

    if (infoWindowRef.current) infoWindowRef.current.close();
    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: '<div style="font-size:13px;font-weight:600;padding:4px 6px;">Selected Location</div>',
    });
    infoWindowRef.current.open(mapRef.current, markerRef.current);
  };

  const initMap = (el) => {
    if (!el || mapRef.current) return;

    const waitForGoogle = (attempts = 0) => {
      if (window.google?.maps) {
        const defaultCenter = { lat: 30.2672, lng: -97.7431 }; // Austin, TX
        mapRef.current = new window.google.maps.Map(el, {
          center: defaultCenter,
          zoom: 5,
          disableDefaultUI: false,
        });

        mapRef.current.addListener("click", (e) => {
          const lat = e.latLng.lat();
          const lng = e.latLng.lng();
          new window.google.maps.Geocoder().geocode({ location: { lat, lng } }, (res, status) => {
            if (status === "OK" && res && res.length > 0) {
              processGeocodeResults(res, lat, lng);
            }
          });
        });

        if (selected) {
          const pos = { lat: Number(selected.lat), lng: Number(selected.lng) };
          placeMarker(pos);
        }
      } else if (attempts < 30) {
        setTimeout(() => waitForGoogle(attempts + 1), 200);
      }
    };
    waitForGoogle();
  };

  useEffect(() => {
    if (!selected || !mapRef.current || !window.google) return;
    const pos = { lat: Number(selected.lat), lng: Number(selected.lng) };
    placeMarker(pos);
  }, [selected]);

  useEffect(() => {
    if (step !== "add") {
      resetMapRefs();
    }
  }, [step]);

  // ═══════════════════════════════════════════════════════
  // Sync address_line_1 when selected changes
  // ═══════════════════════════════════════════════════════
  useEffect(() => {
    if (selected?.address) {
      let city = "", state = "", country = "", zipcode = "";
      let localParts = [];
      if (selected.components) {
        selected.components.forEach((comp) => {
          if (comp.types.includes("locality")) city = comp.long_name;
          if (comp.types.includes("administrative_area_level_1")) state = comp.long_name;
          if (comp.types.includes("country")) country = comp.long_name;
          if (comp.types.includes("postal_code")) zipcode = comp.long_name;

          const ignoreTypes = ["locality", "administrative_area_level_1", "administrative_area_level_2", "country", "postal_code", "postal_code_suffix"];
          if (!comp.types.some(t => ignoreTypes.includes(t))) {
            localParts.push(comp.long_name);
          }
        });
      }

      let line1 = localParts.length > 0 ? localParts.join(", ") : selected.address;

      setFormData((prev) => ({
        ...prev,
        address_line_1: line1,
        ...(city && { city }),
        ...(state && { state }),
        ...(country && { country }),
        ...(zipcode && { zipcode })
      }));
    }
  }, [selected]);

  // ═══════════════════════════════════════════════════════
  // Fetch addresses
  // ═══════════════════════════════════════════════════════
  const fetchAddresses = () => {
    if (!userId) return;
    setListLoading(true);
    setListError("");
    getAddresses(userId)
      .then((res) => {
        const results = Array.isArray(res.data?.results) ? res.data.results : [];
        setAddresses(results);
        
        const defaultAddrStr = localStorage.getItem("defaultAddress");
        let defaultId = defaultAddrStr ? JSON.parse(defaultAddrStr).id : null;
        
        const selectNewest = localStorage.getItem("selectNewestAddress") === "true";
        if (selectNewest && results.length > 0) {
          const newest = [...results].sort((a, b) => (b.id || 0) - (a.id || 0))[0];
          if (newest) {
            localStorage.setItem("defaultAddress", JSON.stringify(newest));
            localStorage.setItem("defaultAddressId", String(newest.id));
            setDefaultAddressId(newest.id);
          }
          localStorage.removeItem("selectNewestAddress");
        } else if (results.length > 0) {
          if (!defaultId || !results.find(a => String(a.id) === String(defaultId))) {
            const latest = results[0];
            localStorage.setItem("defaultAddress", JSON.stringify(latest));
            localStorage.setItem("defaultAddressId", latest.id.toString());
            setDefaultAddressId(latest.id);
          }
        }
      })
      .catch(() => {
        setListError("Failed to load addresses.");
        setAddresses([]);
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => { fetchAddresses(); }, [userId]);

  async function fetchUserProfile() {
    if (!userId) return;
    try {
      const res = await axiosInstance.get(`/user_data/users/${userId}/`);
      setUserProfile(res.data?.results || res.data);
    } catch (error) {
      console.error("Failed to fetch user profile", error);
    }
  }

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  // ═══════════════════════════════════════════════════════
  // Logout
  // ═══════════════════════════════════════════════════════
  const logout = () => {
    ["token", "userid", "name", "store_id", "defaultAddress", "defaultAddressId", "saleAddress", "rentalAddress", "savedZipcode", "verifiedZipcode"].forEach((k) => localStorage.removeItem(k));
    dispatch(clearWishlist());
    window.dispatchEvent(new Event("authChange"));
    toast.success("Logged out");
    navigate("/");
  };

  // ═══════════════════════════════════════════════════════
  // Default Address Handling
  // ═══════════════════════════════════════════════════════
  const [defaultAddressId, setDefaultAddressId] = useState(() => {
    const defaultAddr = localStorage.getItem("defaultAddress");
    return defaultAddr ? JSON.parse(defaultAddr).id : null;
  });

  const handleSetDefault = (addr) => {
    localStorage.setItem("defaultAddress", JSON.stringify(addr));
    localStorage.setItem("defaultAddressId", addr.id.toString());
    setDefaultAddressId(addr.id); 
    toast.success("Default address set");
  }; 

  // ═══════════════════════════════════════════════════════
  // Zipcode check
  // ═══════════════════════════════════════════════════════
  const checkZipcode = async () => {
    if (!zipcodeCheck || zipcodeCheck.length < 5) return;
    setCheckingZip(true);
    setZipcodeStatus(null);
    try {
      const res = await axiosInstance.get(
        `/masters/check-zipcode-availability/?sale_zipcode=${zipcodeCheck}`
      );
      const statusData = res?.data?.sale_zipcode ?? null;
      setZipcodeStatus(statusData);
      if (statusData?.status === "Available") {
        localStorage.setItem("savedZipcode", zipcodeCheck);
      }
    } catch {
      setZipcodeStatus({ status: "Error" });
    } finally {
      setCheckingZip(false);
    }
  };

  // ═══════════════════════════════════════════════════════
  // Address selection for checkout
  // ═══════════════════════════════════════════════════════
  const handleSelectAddressForCheckout = async (addr) => {
    if (!addr.zipcode) {
      toast.error("Selected address doesn't have a zipcode");
      return;
    }

    try {
      let url = `/masters/check-zipcode-availability/?`;
      if (addressType === 'sale') {
        url += `sale_zipcode=${addr.zipcode}`;
      } else if (addressType === 'rental') {
        url += `rental_zipcode=${addr.zipcode}`;
      } else {
        url += `sale_zipcode=${addr.zipcode}`;
      }

      const res = await axiosInstance.get(url);
      const result = res.data;

      const isAvailable =
        addressType === "sale"
          ? result.sale_zipcode?.status === "Available"
          : addressType === "rental"
            ? result.rental_zipcode?.status === "Available"
            : result.sale_zipcode?.status === "Available" && result.rental_zipcode?.status !== "Unavailable";

      if (isAvailable) {
        if (addressType === 'sale') {
          localStorage.setItem('saleAddress', JSON.stringify(addr));
          toast.success("Sale delivery available for this zipcode!");
        } else if (addressType === 'rental') {
          localStorage.setItem('rentalAddress', JSON.stringify(addr));
          toast.success("Rental delivery available for this zipcode!");
        } else {
          localStorage.setItem('saleAddress', JSON.stringify(addr));
          localStorage.setItem('rentalAddress', JSON.stringify(addr));
          toast.success("Delivery available for this zipcode!");
        }
        navigate(-1);
      } else {
        toast.error(`Delivery not available for zipcode: ${addr.zipcode}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error validating zipcode");
    }
  };

  // ═══════════════════════════════════════════════════════
  // Google Places autocomplete
  // ═══════════════════════════════════════════════════════
  const getSuggestions = (val) => {
    if (!window.google || val.length < 2) return setSuggestions([]);
    new window.google.maps.places.AutocompleteService().getPlacePredictions(
      { input: val },
      (preds, status) => (status === "OK" ? setSuggestions(preds) : setSuggestions([]))
    );
  };

  const handleSelect = (place) => {
    setQuery(place.description);
    setSuggestions([]);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: place.place_id }, (res, status) => {
      if (status === "OK" && res && res[0]) {
        const loc = res[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        geocoder.geocode({ location: { lat, lng } }, (revRes, revStatus) => {
          if (revStatus === "OK" && revRes && revRes.length > 0) {
            processGeocodeResults(revRes, lat, lng, place.description);
          } else {
            processGeocodeResults(res, lat, lng, place.description);
          }
        });
      }
    });
  };

  // ═══════════════════════════════════════════════════════
  // Start add flow
  // ═══════════════════════════════════════════════════════
  const startAdd = () => {
    if (zipcodeStatus?.status !== "Available") {
      toast.warning("Please check service availability in your area first.");
      return;
    }
    setEditId(null);
    setGeoError("");
    setFieldErrors({});
    resetLocationPicker();
    setFormData({ 
      ...emptyForm, 
      zipcode: zipcodeCheck,
      name: userProfile?.full_name || "",
      mobileno: userProfile?.mobileno || ""
    });
    setStep("add");
  };

  // ═══════════════════════════════════════════════════════
  // Edit address
  // ═══════════════════════════════════════════════════════
  const startEdit = async (address) => {
    setFieldErrors({});
    setEditId(address.id);
    setFormData({
      name: address.name || "",
      address_line_1: address.address_line_1 || "",
      address_line_2: address.address_line_2 || "",
      flat_no: address.flat_no || "",
      landmark: address.landmark || "",
      zipcode: address.zipcode || "",
      mobileno: address.mobileno || "",
      country: address.country || "USA",
      state: address.state || "",
      city: address.city || "",
      latitude: address.latitude || "",
      longitude: address.longitude || "",
    });
    setSelected({
      lat: address.latitude,
      lng: address.longitude,
      address: address.address_line_1,
    });
    setQuery(address.address_line_1 || "");
    setStep("address");
  };

  // Delete address
  const deleteAddress = async (id) => {
    try {
      await axiosInstance.delete(`/accounts/address/${id}/`);
      toast.success("Address deleted");
      fetchAddresses();
    } catch {
      toast.error("Failed to delete address.");
    }
  };
  // ═══════════════════════════════════════════════════════
  // Submit form
  // ═══════════════════════════════════════════════════════
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFieldErrors({});
    const payload = {
      ...formData,
      latitude: selected?.lat || formData.latitude,
      longitude: selected?.lng || formData.longitude,
      address_line_1: selected?.address || formData.address_line_1,
      user: userId,
    };

    if (!payload.zipcode) {
      toast.error("Please enter a zipcode");
      setSaving(false);
      return;
    }
    try {
      const zipRes = await axiosInstance.get(`/masters/check-zipcode-availability/?sale_zipcode=${payload.zipcode}`);
      if (zipRes?.data?.sale_zipcode?.status !== "Available") {
        toast.error(`Service not available for zipcode: ${payload.zipcode}`);
        setSaving(false);
        return;
      }
    } catch {
      toast.error("Failed to verify zipcode availability.");
      setSaving(false);
      return;
    }

    try {
      if (editId) {
        await updateAddress(editId, payload);
        toast.success("Address updated");
      } else {
        const res = await axiosInstance.post("/accounts/address/", payload, {
          headers: { "Content-Type": "application/json" },
        });
        toast.success("Address added");
        const newObj = res?.data?.data || res?.data;
        if (newObj && newObj.id) {
          localStorage.setItem("defaultAddress", JSON.stringify(newObj));
          localStorage.setItem("defaultAddressId", String(newObj.id));
          setDefaultAddressId(newObj.id);
        } else {
          localStorage.setItem("selectNewestAddress", "true");
        }
      }
      
      if (payload.zipcode) {
        localStorage.setItem("savedZipcode", payload.zipcode);
        localStorage.setItem("verifiedZipcode", payload.zipcode);
        setZipcodeCheck(payload.zipcode);
        setZipcodeStatus({ status: "Available" });
      }
      
      setStep("default");
      setEditId(null);
      resetLocationPicker();
      setFormData({ ...emptyForm });
      fetchAddresses();
    } catch (error) {
      if (error?.response?.status === 400 && error?.response?.data && typeof error.response.data === "object") {
        setFieldErrors(error.response.data);
        const firstField = Object.keys(error.response.data)[0];
        const firstVal = error.response.data[firstField];
        const firstMsg = Array.isArray(firstVal) ? firstVal[0] : firstVal;
        toast.error(typeof firstMsg === "string" ? `${firstField}: ${firstMsg}` : "Please check form fields for errors.");
      } else {
        toast.error(error?.response?.data?.message || "Failed to save address. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ═══════════════════════════════════════════════════════
  // Not logged in
  // ═══════════════════════════════════════════════════════
  if (!userId) {
    return (
      <main className="account-page">
        <div className="account-wrap">
          <section className="account-empty">
            <p>Please login to manage your addresses.</p>
            <button className="account-primary-btn" type="button" onClick={() => navigate("/")}>
              Go home
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="account-page">
      <div className="account-shell">

        {/* ── sidebar ── */}
        <aside className="account-sidebar">
          <p style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiUser size={14} /> Account</p>
          <strong>{userName}</strong>
          <button type="button" onClick={() => navigate("/orders")}>Orders</button>
          <button className="active" type="button">Address</button>
          <button type="button" onClick={() => navigate("/wishlist")}>Wishlist</button>
          <button type="button" className="logout-btn" onClick={logout}>Logout</button>
        </aside>

        {/* ── main content ── */}
        <section className="account-content">
          <div className="account-heading compact">
            <div>
              <span>Account</span>
              <h1>
                {step === "default" && "My Addresses"}
                {step === "add" && "Add Address"}
                {step === "address" && (editId ? "Edit Address" : "Add Address")}
              </h1>
            </div>
            {step !== "default" && (
              <button
                className="account-secondary-btn"
                type="button"
                onClick={() => {
                  setStep("default");
                  setEditId(null);
                  resetLocationPicker();
                  setFormData({ ...emptyForm });
                }}
              >
                ← Back
              </button>
            )}
          </div>

          {/* ════════════ STEP: default ════════════ */}
          {step === "default" && (
            <>
              {/* zipcode checker */}
              <div className="zip-checker">
                <p className="zip-checker-label">Check service availability in your area</p>
                <div className="zip-row">
                  <input
                    className="zip-input"
                    type="text"
                    placeholder="Enter pincode"
                    maxLength={5}
                    value={zipcodeCheck}
                    onChange={(e) => {
                      setZipcodeCheck(e.target.value.replace(/\D/g, "").slice(0, 5));
                      setZipcodeStatus(null);
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") checkZipcode(); }}
                  />
                  <button
                    className="zip-check-btn"
                    type="button"
                    onClick={checkZipcode}
                    disabled={checkingZip || zipcodeCheck.length < 5}
                  >
                    {checkingZip ? "Checking..." : "Check"}
                  </button>
                </div>
                {zipcodeStatus && (
                  <p className={`zip-result ${zipcodeStatus.status === "Available" ? "zip-ok" : "zip-fail"}`}>
                    {zipcodeStatus.status === "Available"
                      ? `✓ Service available at ${zipcodeCheck}`
                      : "✗ Service not available in this area"}
                  </p>
                )}
              </div>

              {/* add button — only enabled if zip is available */}
              <button
                className="account-primary-btn"
                type="button"
                onClick={startAdd}
                style={{ marginBottom: 24 }}
              >
                + Add new address
              </button>

              {/* address list */}
              {listLoading && <div className="account-empty">Loading addresses...</div>}
              {listError && <div className="account-alert">{listError}</div>}
              {!listLoading && (
                <div className="account-grid">
                  {addresses.length > 0 ? (
                    addresses.map((addr) => (
                      <article className="account-card address-card" key={addr.id}>
                        <div>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <h2 
                              style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", margin: 0 }} 
                              onClick={() => handleSetDefault(addr)}
                            >
                              {String(defaultAddressId) === String(addr.id) ? (
                                <FaCheckCircle style={{ color: "#FE7900", fontSize: "1.1rem" }} />
                              ) : (
                                <FaRegCircle style={{ color: "#ccc", fontSize: "1.1rem" }} />
                              )}
                              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <IoLocationOutline /> {addr.name || "Address"}
                              </span>
                            </h2>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                              {String(defaultAddressId) === String(addr.id) && (
                                <span style={{ fontSize: "11px", backgroundColor: "#e6f4ea", color: "#1e8e3e", padding: "4px 8px", borderRadius: "4px", fontWeight: "bold", height: "fit-content" }}>
                                  DEFAULT
                                </span>
                              )}
                              <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                  className="account-icon-btn"
                                  type="button"
                                  onClick={() => startEdit(addr)}
                                  aria-label="Edit"
                                >
                                  <FiEdit2 />
                                </button>
                                <button
                                  className="account-icon-btn account-icon-btn-danger"
                                  type="button"
                                  onClick={() => deleteAddress(addr.id)}
                                  aria-label="Delete"
                                >
                                  <FiTrash2 />
                                </button>
                              </div>
                            </div>
                          </div>
                          <p style={{ marginTop: "12px" }}>
                            {[addr.flat_no, addr.address_line_1, addr.address_line_2, addr.landmark]
                              .filter(Boolean).join(", ")}
                          </p>
                          <p>
                            {[addr.city, addr.state, addr.country].filter(Boolean).join(", ")}
                            {addr.zipcode ? ` - ${addr.zipcode}` : ""}
                          </p>
                          <small>Contact: {addr.mobileno || "N/A"}</small>
                          {addressType && defaultAddressId === addr.id && (
                            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
                              <button
                                className="stroke-delivery-btn"
                                type="button"
                                onClick={() => handleSelectAddressForCheckout(addr)}
                              >
                                Select address for delivery
                              </button>
                            </div>
                          )}
                        </div>
                      </article>
                    ))
                  ) : (
                    <div className="account-empty">No addresses found.</div>
                  )}
                </div>
              )}
            </>
          )}

          {/* ════════════ STEP: add (search + map) ════════════ */}
          {step === "add" && (
            <div className="location-picker-panel">
              {geoLocating && (
                <div className="geo-locating-banner">
                  <span className="geo-spinner" />
                  Detecting your current location…
                </div>
              )}

              <div className="location-search-bar">
                <FiSearch className="location-search-icon" />
                <input
                  ref={searchRef}
                  className="location-search-input"
                  value={query}
                  placeholder="Search for a place"
                  onChange={(e) => {
                    setQuery(e.target.value);
                    getSuggestions(e.target.value);
                  }}
                  disabled={geoLocating}
                  autoFocus
                />
                {suggestions.length > 0 && (
                  <div className="addr-suggestions">
                    {suggestions.map((s) => (
                      <div
                        key={s.place_id}
                        className="addr-suggestion-item"
                        onClick={() => handleSelect(s)}
                      >
                        <IoLocationOutline className="addr-sugg-icon" />
                        <span>{s.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="use-location-btn"
                onClick={detectCurrentLocation}
                disabled={geoLocating}
              >
                <IoLocationOutline className="use-location-icon" />
                {geoLocating ? "Detecting…" : "Use Current Location"}
              </button>

              {geoError && <p className="geo-error-msg">{geoError}</p>}

              <div ref={initMap} className="location-map-view" />

              {selected && (
                <div className="location-confirm-bar">
                  <div className="location-confirm-address">
                    <IoLocationOutline className="addr-sugg-icon" />
                    <span>{selected.address}</span>
                  </div>
                  <button
                    type="button"
                    className="account-primary-btn location-confirm-btn"
                    onClick={() => {
                      setSuggestions([]);
                      setStep("address");
                    }}
                  >
                    Confirm Location
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ════════════ STEP: address form ════════════ */}
          {step === "address" && (
            <form className="account-card addr-form" onSubmit={handleSubmit}>

              {/* selected location preview */}
              {selected?.address && (
                <div className="addr-location-preview">
                  <div className="addr-location-text">
                    <IoLocationOutline />
                    <span>{selected.address}</span>
                  </div>
                  <button
                    type="button"
                    className="addr-change-link"
                    onClick={() => {
                      setQuery("");
                      setSuggestions([]);
                      setSelected(null);
                      setGeoError("");
                      resetMapRefs();
                      setStep("add");
                    }}
                  >
                    Change
                  </button>
                </div>
              )}

              <div className="address-form-grid">
                <label>
                  Full Name *
                  <input value={formData.name} required
                    onChange={(e) => handleFieldChange("name", e.target.value)} />
                  {renderFieldError("name")}
                </label>
                <label>
                  Mobile *
                  <input value={formData.mobileno} maxLength={10} required
                    onChange={(e) => handleFieldChange("mobileno", e.target.value.replace(/\D/g, "").slice(0, 10))} />
                  {renderFieldError("mobileno")}
                </label>
                <label className="full">
                  Address line 1 *
                  <input value={formData.address_line_1} required
                    onChange={(e) => handleFieldChange("address_line_1", e.target.value)} />
                  {renderFieldError("address_line_1")}
                </label>
                <label className="full">
                  Address line 2
                  <input value={formData.address_line_2}
                    onChange={(e) => handleFieldChange("address_line_2", e.target.value)} />
                  {renderFieldError("address_line_2")}
                </label>
                <label>
                  Flat / House no
                  <input value={formData.flat_no}
                    onChange={(e) => handleFieldChange("flat_no", e.target.value)} />
                  {renderFieldError("flat_no")}
                </label>
                <label>
                  Landmark
                  <input value={formData.landmark}
                    onChange={(e) => handleFieldChange("landmark", e.target.value)} />
                  {renderFieldError("landmark")}
                </label>
                <label>
                  City *
                  <input value={formData.city} required
                    onChange={(e) => handleFieldChange("city", e.target.value)} />
                  {renderFieldError("city")}
                </label>
                <label>
                  State *
                  <input value={formData.state} required
                    onChange={(e) => handleFieldChange("state", e.target.value)} />
                  {renderFieldError("state")}
                </label>
                <label>
                  Country *
                  <input value={formData.country} required
                    onChange={(e) => handleFieldChange("country", e.target.value)} />
                  {renderFieldError("country")}
                </label>
                <label>
                  Zipcode *
                  <input value={formData.zipcode} maxLength={5} required
                    onChange={(e) => handleFieldChange("zipcode", e.target.value.replace(/\D/g, "").slice(0, 6))} />
                  {renderFieldError("zipcode")}
                </label>
              </div>

              <div className="address-form-actions">
                <button
                  type="button"
                  className="account-secondary-btn"
                  onClick={() => { setStep("default"); setEditId(null); setFieldErrors({}); setFormData({ ...emptyForm }); }}
                >
                  Cancel
                </button>
                <button type="submit" className="account-primary-btn" disabled={saving}>
                  {saving ? "Saving..." : editId ? "Update Address" : "Save Address"}
                </button>
              </div>
            </form>
          )}

        </section>
      </div>
    </main>
  );
}

export default AddressesPage;